
import React, { useState, useEffect } from 'react';
import { Plus, History, Edit3, Save, X, Zap, Target, Info, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { api } from '../../../../src/services/api';
import { logUsageEvent } from '../../../../src/lib/logger';
import { formatCurrency, getTarifaVigente, getComissaoPercentualVendedor } from '../../../../src/lib/comissaoUtils';
import type { TarifaEnergia, ComissaoVendedor, Profile } from '../../../../types';

interface Props {
  tarifas: TarifaEnergia[];
  metas: ComissaoVendedor[];
  vendedores: Profile[];
  mes: number;
  ano: number;
  canEdit: boolean;
  onRefresh: () => void;
}

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const ConfiguracoesTab: React.FC<Props> = ({ tarifas, metas, vendedores, mes, ano, canEdit, onRefresh }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [showTarifaForm, setShowTarifaForm] = useState(false);
  const [novaTarifaValor, setNovaTarifaValor] = useState('');
  const [novaTarifaInicio, setNovaTarifaInicio] = useState('');
  const [novaTarifaDesc, setNovaTarifaDesc] = useState('');
  const [savingTarifa, setSavingTarifa] = useState(false);

  const [editingMeta, setEditingMeta] = useState<string | null>(null);
  const [editMetaKwh, setEditMetaKwh] = useState('');
  const [editSalario, setEditSalario] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const [previousMetas, setPreviousMetas] = useState<Map<string, { meta_kwh: number; salario_base: number }> | null>(null);

  useEffect(() => {
    const hasConfigured = metas.some(m => (m.meta_kwh || 0) > 0 || (m.salario_base || 0) > 0);
    if (!hasConfigured && vendedores.length > 0) {
      api.folhaPagamento.getLastMetasVendedores(mes, ano)
        .then(map => setPreviousMetas(map.size > 0 ? map : null))
        .catch(() => setPreviousMetas(null));
    } else {
      setPreviousMetas(null);
    }
  }, [metas, vendedores, mes, ano]);

  const handleAddTarifa = async () => {
    if (!novaTarifaValor || !novaTarifaInicio) { addToast('Preencha valor e data', 'error'); return; }
    setSavingTarifa(true);
    try {
      await api.folhaPagamento.createTarifa({
        valor: parseFloat(novaTarifaValor),
        vigencia_inicio: novaTarifaInicio,
        descricao: novaTarifaDesc || `Tarifa ${new Date(novaTarifaInicio).getFullYear()}`,
        created_by: profile?.id,
      });
      addToast('Nova tarifa cadastrada!', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'criar_tarifa_energia', origem: 'acao' });
      setShowTarifaForm(false); setNovaTarifaValor(''); setNovaTarifaInicio(''); setNovaTarifaDesc('');
      onRefresh();
    } catch { addToast('Erro ao salvar tarifa', 'error'); }
    finally { setSavingTarifa(false); }
  };

  const startEditMeta = (vendedorId: string) => {
    const existing = metas.find(m => m.vendedor_id === vendedorId);
    const inherited = previousMetas?.get(vendedorId);
    setEditingMeta(vendedorId);
    const metaVal = existing && existing.meta_kwh > 0 ? existing.meta_kwh : (inherited?.meta_kwh || 0);
    const salVal = existing && existing.salario_base > 0 ? existing.salario_base : (inherited?.salario_base || 0);
    setEditMetaKwh(metaVal > 0 ? String(metaVal) : '');
    setEditSalario(salVal > 0 ? String(salVal) : '');
  };

  const handleSaveMeta = async (vendedorId: string) => {
    setSavingMeta(true);
    try {
      const existing = metas.find(m => m.vendedor_id === vendedorId);
      const newMeta = parseFloat(editMetaKwh) || 0;
      const newSalario = parseFloat(editSalario) || 0;
      let comissaoData: Partial<ComissaoVendedor>;
      if (existing) {
        const totalKwh = existing.total_kwh_vendido || 0;
        const percentualAtingimento = newMeta > 0 ? (totalKwh / newMeta) * 100 : 0;
        const pComissao = getComissaoPercentualVendedor(percentualAtingimento);
        const valorComissao = totalKwh * (pComissao / 100) * (existing.tarifa_energia_valor || 1.16);
        const valorBonus = existing.valor_bonus || 0;
        const valorTotal = valorComissao + valorBonus + newSalario;
        comissaoData = {
          vendedor_id: vendedorId, mes, ano,
          meta_kwh: newMeta, salario_base: newSalario,
          percentual_atingimento: percentualAtingimento, percentual_comissao: pComissao,
          valor_comissao_kwh: valorComissao, valor_total: valorTotal,
          tarifa_energia_valor: existing.tarifa_energia_valor || 1.16,
          tarifa_energia_id: existing.tarifa_energia_id,
          total_kwh_vendido: totalKwh, valor_bonus: valorBonus,
          status: existing.status || 'calculado', observacoes: existing.observacoes,
        };
      } else {
        const mesStr = String(mes).padStart(2, '0');
        const tarifa = getTarifaVigente(tarifas, `${ano}-${mesStr}-01`);
        comissaoData = {
          vendedor_id: vendedorId, mes, ano,
          meta_kwh: newMeta, salario_base: newSalario,
          total_kwh_vendido: 0, percentual_atingimento: 0, percentual_comissao: 7,
          valor_comissao_kwh: 0, valor_bonus: 0, valor_total: newSalario,
          tarifa_energia_valor: tarifa?.valor || 1.16, tarifa_energia_id: tarifa?.id,
          status: 'calculado',
        };
      }
      await api.folhaPagamento.upsertComissaoVendedor(comissaoData);
      addToast('Configurações aplicadas à comissão com sucesso!', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'folha_pagamento', acao: 'salvar_meta_salario_vendedor', origem: 'acao' });
      setEditingMeta(null);
      onRefresh();
    } catch (err: any) {
      console.error('[ConfiguracoesTab] Erro ao salvar:', err);
      addToast(`Erro ao salvar: ${err?.message || JSON.stringify(err) || 'Desconhecido'}`, 'error');
    } finally { setSavingMeta(false); }
  };

  const tarifaVigente = tarifas.length > 0 ? tarifas[0] : null;

  return (
    <div className="space-y-8">

      {/* ══════ Tarifa de Energia ══════ */}
      <section style={{ animation: 'fadeSlideUp 0.4s ease-out' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#F6A937]/15 flex items-center justify-center">
              <Zap size={16} className="text-[#F6A937]" />
            </div>
            Tarifa de Energia
          </h3>
          {canEdit && !showTarifaForm && (
            <button onClick={() => setShowTarifaForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#F6A937] to-[#d97706] text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all">
              <Plus size={14} /> Nova Tarifa
            </button>
          )}
        </div>

        {/* Active tariff highlight */}
        {tarifaVigente && (
          <div className="relative overflow-hidden bg-[#212913] rounded-2xl p-5 mb-4 shadow-lg">
            <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/5 blur-sm pointer-events-none" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.15em] mb-1">Tarifa Vigente</p>
                <p className="text-3xl font-black text-white tabular-nums">R$ {tarifaVigente.valor.toFixed(2)}</p>
                <p className="text-xs text-white/50 mt-1">
                  {tarifaVigente.descricao || 'Sem descrição'} · Desde {new Date(tarifaVigente.vigencia_inicio + 'T00:00:00').toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[#F6A937]/20 flex items-center justify-center">
                <Zap size={24} className="text-[#F6A937]" />
              </div>
            </div>
          </div>
        )}

        {/* Add tariff form */}
        {showTarifaForm && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-[#F6A937]/30 dark:border-[#F6A937]/20 p-5 mb-4 shadow-sm" style={{ animation: 'fadeSlideUp 0.3s ease-out' }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-sm text-gray-900 dark:text-white">Cadastrar Nova Tarifa</h4>
              <button onClick={() => setShowTarifaForm(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"><X size={16} /></button>
            </div>
            <div className="bg-[#F6A937]/8 dark:bg-[#F6A937]/5 border border-[#F6A937]/20 rounded-lg p-3 mb-4">
              <p className="text-xs text-[#b85c00] dark:text-[#F6A937] font-medium">
                ⚠️ Criar uma nova tarifa <strong>não altera</strong> cálculos anteriores. Apenas novos cálculos usarão este valor.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Valor (R$)</label>
                <input type="number" step="0.01" value={novaTarifaValor} onChange={e => setNovaTarifaValor(e.target.value)}
                  placeholder="1.16" className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 transition-shadow" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Vigência a partir de</label>
                <input type="date" value={novaTarifaInicio} onChange={e => setNovaTarifaInicio(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 transition-shadow" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Descrição</label>
                <input type="text" value={novaTarifaDesc} onChange={e => setNovaTarifaDesc(e.target.value)}
                  placeholder="Tarifa 2026" className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 transition-shadow" />
              </div>
            </div>
            <button onClick={handleAddTarifa} disabled={savingTarifa}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#F6A937] to-[#d97706] text-white rounded-xl text-sm font-bold disabled:opacity-50 hover:shadow-lg transition-all">
              <Save size={14} /> {savingTarifa ? 'Salvando...' : 'Criar Tarifa'}
            </button>
          </div>
        )}

        {/* Tariff history */}
        {tarifas.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40">
              <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <History size={12} /> Histórico de Tarifas
              </h4>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
              {tarifas.slice(1).map(t => (
                <div key={t.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t.descricao || '-'}</span>
                  </div>
                  <div className="flex items-center gap-5 text-sm">
                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                      {new Date(t.vigencia_inicio + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </span>
                    <span className="font-bold text-gray-600 dark:text-gray-300 tabular-nums">R$ {t.valor.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tarifas.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-10 text-center">
            <Zap size={32} className="mx-auto text-gray-200 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma tarifa cadastrada</p>
          </div>
        )}
      </section>

      {/* ══════ Metas por Vendedor ══════ */}
      <section style={{ animation: 'fadeSlideUp 0.55s ease-out' }}>
        <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-[#5F6C37]/10 dark:bg-[#5F6C37]/20 flex items-center justify-center">
            <Target size={16} className="text-[#5F6C37]" />
          </div>
          Metas Mensais — {MESES[mes - 1]} {ano}
        </h3>

        {previousMetas && (
          <div className="bg-[#F6A937]/8 dark:bg-[#F6A937]/5 border border-[#F6A937]/25 dark:border-[#F6A937]/15 rounded-xl p-4 mb-4 flex items-start gap-3" style={{ animation: 'fadeSlideUp 0.5s ease-out' }}>
            <Info size={16} className="text-[#F6A937] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#b85c00] dark:text-[#F6A937]">Valores herdados do mês anterior</p>
              <p className="text-xs text-[#b85c00]/70 dark:text-[#F6A937]/60 mt-0.5">
                Como este mês ainda não tem metas configuradas, os valores do último mês com dados foram aplicados automaticamente. Edite para personalizar.
              </p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60">
                  <th className="text-left px-5 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Vendedor</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Meta (kWh)</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-500 dark:text-gray-400 text-[11px] uppercase tracking-wide">Salário Base</th>
                  {canEdit && <th className="px-4 py-3 w-24"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                {vendedores.map((v, idx) => {
                  const meta = metas.find(m => m.vendedor_id === v.id);
                  const isEditing = editingMeta === v.id;
                  const inherited = previousMetas?.get(v.id);
                  const displayMeta = (meta && meta.meta_kwh > 0) ? meta.meta_kwh : (inherited?.meta_kwh || 0);
                  const displaySalario = (meta && meta.salario_base > 0) ? meta.salario_base : (inherited?.salario_base || 0);
                  const isInherited = inherited && (!meta || (meta.meta_kwh === 0 && meta.salario_base === 0));
                  const initials = v.full_name?.split(' ').filter(Boolean).map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'NA';

                  return (
                    <tr key={v.id} className="hover:bg-gray-50/70 dark:hover:bg-gray-700/20 transition-colors"
                      style={{ animation: `fadeSlideUp ${0.4 + idx * 0.05}s ease-out` }}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-[rgba(95,108,55,0.9)] flex items-center justify-center text-[10px] font-black text-white flex-shrink-0 shadow-sm">
                            {initials}
                          </div>
                          <div>
                            <span className="font-semibold text-sm text-gray-900 dark:text-white">{v.full_name}</span>
                            {isInherited && (
                              <span className="flex items-center gap-1 text-[10px] text-[#F6A937] mt-0.5 font-medium">
                                <Info size={9} /> Herdado
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {isEditing ? (
                          <input type="number" value={editMetaKwh} onChange={e => setEditMetaKwh(e.target.value)}
                            className="w-32 px-3 py-2 border border-[#5F6C37]/30 dark:border-[#5F6C37]/40 rounded-xl bg-white dark:bg-gray-900 text-sm text-right text-gray-900 dark:text-white ml-auto block focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 transition-shadow" autoFocus />
                        ) : (
                          <span className={`tabular-nums font-semibold ${isInherited ? 'text-[#F6A937] italic' : 'text-gray-700 dark:text-gray-300'}`}>
                            {displayMeta > 0 ? `${displayMeta.toLocaleString('pt-BR')} kWh` : '—'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {isEditing ? (
                          <input type="number" value={editSalario} onChange={e => setEditSalario(e.target.value)}
                            className="w-32 px-3 py-2 border border-[#5F6C37]/30 dark:border-[#5F6C37]/40 rounded-xl bg-white dark:bg-gray-900 text-sm text-right text-gray-900 dark:text-white ml-auto block focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 transition-shadow" />
                        ) : (
                          <span className={`tabular-nums font-semibold ${isInherited ? 'text-[#F6A937] italic' : 'text-gray-700 dark:text-gray-300'}`}>
                            {displaySalario > 0 ? formatCurrency(displaySalario) : '—'}
                          </span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3.5">
                          {isEditing ? (
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => setEditingMeta(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"><X size={14} /></button>
                              <button onClick={() => handleSaveMeta(v.id)} disabled={savingMeta}
                                className="p-1.5 text-[#5F6C37] hover:bg-[#5F6C37]/10 dark:hover:bg-[#5F6C37]/20 rounded-lg transition-colors">
                                {savingMeta ? <span className="w-3.5 h-3.5 border-2 border-[#5F6C37] border-t-transparent rounded-full animate-spin block" /> : <Save size={14} />}
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => startEditMeta(v.id)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                              <Edit3 size={14} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {vendedores.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-10 text-center text-gray-400 text-sm">Nenhum vendedor ativo encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ConfiguracoesTab;
